import { Link } from "react-router-dom";
import { MarketingHeader } from "@/components/marketing/MarketingHeader";
import {
  ArrowRight, Clock, MapPin, ShieldAlert, HardHat, ClipboardCheck, QrCode,
  MessageSquare, Truck, Receipt, WifiOff, CalendarDays, Briefcase, Banknote,
  Map, Wrench, Award, FileCheck2, FileText, FileSignature, Library, Package,
  Wallet, Sparkles, FolderKanban, LayoutDashboard, Users, Globe, CreditCard,
  Smartphone, Lock, Check, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BrandMark } from "@/components/BrandMark";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "./FAQ";
import { PlatformMap } from "@/components/marketing/PlatformMap";

const GROUPS = [
  {
    id: "field",
    tag: "01 / On the tools",
    title: "For the field crew",
    blurb: "A phone-first app that works in gloves, in glare, and with no reception. Everything a worker needs for the day on one screen.",
    features: [
      { icon: Clock, title: "GPS clock-in & timesheets", desc: "One-tap clock in/out stamped with location. Hours flow straight into the weekly timesheet — no paper, no chasing." },
      { icon: HardHat, title: "Machine pre-starts", desc: "Pass/fail checklists with photos and signatures. Designated mechanical failures create damage reports for office review." },
      { icon: QrCode, title: "QR site sign-in", desc: "Print a QR poster for the gate. Crew get 1-tap sign-in; visitors and subbies sign in with no app needed." },
      { icon: Briefcase, title: "Daily run-sheet", desc: "Today's job, start time, directions, who's on the crew, assigned tasks and the SSSP — all on the home screen." },
      { icon: ShieldAlert, title: "Hazard & incident reports", desc: "Record the issue with a description and photos from the phone for office review." },
      { icon: ClipboardCheck, title: "Toolbox talks", desc: "Run and sign off toolbox talks on site with digital attendance signatures." },
      { icon: MessageSquare, title: "Crew chat", desc: "Job messaging with @mentions, photos and push notifications — keep WhatsApp out of your business." },
      { icon: Truck, title: "Truck running sheets", desc: "Drivers log loads, tip sites and quantities as they go — ready for billing at the end of the day." },
      { icon: Receipt, title: "Contractor invoicing", desc: "Subbies turn their approved hours into an invoice from the app in a couple of taps." },
      { icon: CalendarDays, title: "Leave requests", desc: "Book annual, sick or unpaid leave from the phone; approvals flow back instantly." },
      { icon: WifiOff, title: "Works offline", desc: "No reception on site? Forms, photos and timesheets queue on the phone and sync when coverage returns." },
      { icon: Smartphone, title: "Nothing to install", desc: "It's a PWA — add it to the home screen from the browser on any iPhone or Android. Updates arrive automatically." },
    ],
  },
  {
    id: "office",
    tag: "02 / In the office",
    title: "For the office & project managers",
    blurb: "Run jobs, people, plant and money from one dashboard — with live data coming in from the field all day.",
    features: [
      { icon: Briefcase, title: "Job management & dispatch", desc: "Create jobs, assign crews and machines, dispatch tasks to specific workers and watch progress live." },
      { icon: Clock, title: "Timesheet approvals & payroll", desc: "Approve the week in one screen, then push hours to Xero Payroll in one click. Holidays Act aware." },
      { icon: Map, title: "Live crew map", desc: "See where your crews clocked in across Auckland (or the country) on a live map." },
      { icon: FolderKanban, title: "Project control hub", desc: "One command centre per job: daily logs, drawings, photos, punch lists, tasks, QR sign-in, variations, budget and client portal." },
      { icon: Wrench, title: "Fleet management", desc: "WoF, rego and service tracking with hour-meter servicing, pre-start history and damage reports per machine." },
      { icon: Award, title: "Competency tracking", desc: "Worker competency and certificate records with expiry dates and records due for review." },
      { icon: FileCheck2, title: "SSSP builder", desc: "Draft and edit site-specific plans, or upload an existing job SSSP for worker sign-off." },
      { icon: FileText, title: "Custom digital forms", desc: "Build your own permits, checklists and QA forms with photos, signatures and job links." },
      { icon: FileSignature, title: "Quotes, variations & invoices", desc: "Price work from your rate library, capture variations from site, invoice with progress claims — and sync it all to Xero." },
      { icon: Library, title: "Rate library & build-ups", desc: "Standard civil rates with cost build-ups so every estimator prices the same way." },
      { icon: Package, title: "Supplier bills & materials", desc: "Scan supplier invoices to the right job, and let the crew order materials from the yard." },
      { icon: Wallet, title: "Job budgets vs actual", desc: "Live budget health per job with alerts before you blow the number — labour, plant and materials." },
      { icon: Sparkles, title: "AI daily site diary", desc: "The day's timesheets, photos, weather and notes summarised into a professional diary entry automatically." },
      { icon: LayoutDashboard, title: "Alerts & announcements", desc: "A dashboard of what needs attention — unpaid invoices, expiring certs, fleet due — plus a team noticeboard." },
    ],
  },
  {
    id: "clients",
    tag: "03 / Your customers",
    title: "For your clients",
    blurb: "Give clients a professional window into their job — without another phone call.",
    features: [
      { icon: Globe, title: "Client portal", desc: "Clients log in to see job progress, site photos and daily diaries. You control exactly what each client sees." },
      { icon: FileSignature, title: "Online variation approvals", desc: "Send priced variations to the client portal for one-click approval — no more \"he said, she said\"." },
      { icon: Receipt, title: "Invoices & quotes online", desc: "Clients view invoices in the portal and accept quotes from a public link." },
      { icon: MessageSquare, title: "Client messages", desc: "Comments from the client land with the office team — keeping the record on the job." },
    ],
  },
  {
    id: "platform",
    tag: "04 / Under the hood",
    title: "Integrations & platform",
    blurb: "NZ-made, secure, and connected to the tools you already use.",
    features: [
      { icon: Banknote, title: "Xero integration", desc: "Official OAuth connection — timesheets to Xero Payroll, invoices and quotes synced two ways." },
      { icon: CreditCard, title: "Simple subscription", desc: "One flat monthly fee for the whole crew — no per-seat billing. 7-day free trial, cancel anytime, export your data whenever." },
      { icon: Users, title: "Roles & permissions", desc: "Admin, office, field, mechanic and quantity surveyor roles — everyone sees exactly what they should." },
      { icon: Lock, title: "Your data, protected", desc: "Encrypted in transit and at rest, hosted with daily backups. Your org's data is completely isolated." },
      { icon: Sparkles, title: "Always improving", desc: "New features ship weekly — office users see a what's-new round-up right in the app." },
      { icon: BookOpen, title: "Free migration & NZ support", desc: "Send us a CSV from your old system and we'll load it for free. Support that knows what a pre-start is." },
    ],
  },
];


export default function Features() {
  return (
    <div className="web-seo bg-[#f6f5f1] text-[#142635] min-h-screen" data-testid="features-page">
      <SEO
        title="All Features — Timesheets, Safety, Fleet, Jobs & More | Fieldlink NZ"
        description="Everything inside Fieldlink: GPS timesheets, machine pre-starts, QR site sign-in, SSSPs, fleet & cert tracking, quotes, variations, invoicing, Xero payroll, client portal and more. NZ-made for civil construction."
        path="/features"
      />
      <MarketingHeader />

      {/* Hero */}
      <section className="border-b border-[#cbd3d5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="label-tag !text-[#a63c0b] mb-3">/ Everything inside Fieldlink</div>
          <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            One app for the whole crew — site, office & client
          </h1>
          <p className="text-[#52616c] text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
            Fieldlink replaces the paper timesheets, the whiteboard, the WhatsApp groups and the
            spreadsheet graveyard. Here's everything you get from day one of your free trial.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Link to="/register" data-testid="features-hero-cta">
              <Button className="rounded-md rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-6 h-12">
                Start 7-day free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" data-testid="features-hero-pricing">
              <Button variant="outline" className="rounded-md border-[#cbd3d5] bg-white hover:bg-white text-[#142635] hover:text-[#142635] font-bold uppercase tracking-wider px-6 h-12">
                See pricing
              </Button>
            </Link>
          </div>
          {/* Jump links */}
          <div className="flex flex-wrap gap-2 mt-8">
            {GROUPS.map((g) => (
              <a key={g.id} href={`#${g.id}`}
                 className="rounded-md text-[11px] font-bold uppercase tracking-widest text-[#465562] border border-[#cbd3d5] px-3 py-1.5 hover:border-[#FF5F15] hover:text-[#a63c0b] transition-colors"
                 data-testid={`features-jump-${g.id}`}>
                {g.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      <PlatformMap />

      {/* Feature groups */}
      {GROUPS.map((g, gi) => (
        <section key={g.id} id={g.id} className={gi % 2 === 1 ? "bg-[#e9eeec] border-y border-[#cbd3d5]" : "bg-[#f6f5f1]"}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
            <div className="label-tag !text-[#a63c0b] mb-2">{g.tag}</div>
            <h2 className="font-display font-black uppercase tracking-tight text-lg md:text-lg">{g.title}</h2>
            <p className="text-[#52616c] text-sm md:text-base mt-2 max-w-2xl">{g.blurb}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white rounded-lg overflow-hidden border border-[#cbd3d5] mt-8">
              {g.features.map((f) => {
                const Icon = f.icon;
                return (
                  <div key={f.title} className="bg-white p-6 group hover:bg-[#10151D] transition-colors" data-testid={`feature-card-${g.id}-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <Icon className="w-6 h-6 text-[#a63c0b]" strokeWidth={1.8} />
                    <h3 className="font-display font-bold uppercase tracking-wide text-[15px] mt-4">{f.title}</h3>
                    <p className="text-sm text-[#52616c] mt-1.5 leading-relaxed">{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="bg-white text-[#142635]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 text-center">
          <div className="label-tag !text-[#a63c0b] mb-3">/ Ready when you are</div>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl leading-tight">
            Try every feature free for 7 days
          </h2>
          <p className="text-[#52616c] mt-3 max-w-xl mx-auto text-sm md:text-base">
            No credit card to start. Add your crew, run a real week of timesheets and see the difference.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/register" data-testid="features-footer-cta">
              <Button className="rounded-md rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] font-bold uppercase tracking-wider px-8 h-12">
                Get started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:support@fieldlink.co.nz?subject=Fieldlink%20demo" className="text-sm text-[#465562] hover:text-[#142635] underline underline-offset-4">
              or book a demo
            </a>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-[11px] font-mono uppercase tracking-widest text-[#52616c]">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#a63c0b]" /> NZ-made</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#a63c0b]" /> Xero connected</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#a63c0b]" /> Works offline</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#a63c0b]" /> Cancel anytime</span>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
